import { useCallback, useContext } from 'react';

import { Updater } from '@tanstack/react-table';
import isEqual from 'fast-deep-equal';
import toast from 'react-hot-toast';

import { useMutation } from '@apollo/client';

import { AssetClass as GqlAssetClass } from '__generated__/globalTypes';
import { AssetClassToLoanType } from '../app-level/config/assetClassTypes';
import {
  DataContext,
  DataContextActionType,
  GlobalTableOrderConfig,
  marketHomeTableColumnsOrder,
} from '../context/DataContext';
import { executedTradesColumnOrder } from '../features/deals/Executed/Table.config';
import { activeTradesAllTradesColumnOrder } from '../features/deals/InProgress/ActiveListingsTable.config';
import {
  assetClassToColumnsConfig,
  autoColumnsOrder,
  creColumnsOrder,
  helocColumnsOrder,
  homeARMColumnsOrder,
  marineColumnsOrder,
  unsecpersonalColumnsOrder,
} from '../features/drilldown/LoanDatatable/loanTable.config';
import { listingColsOrder, ListingKey } from '../features/OnMarket/marketplace.config';
import { activeTradesBidsListingColsOrder } from '../features/pages/marketplace/ActiveTrades/bidsTable.config';
import { AddUserSetting } from '../mutation/__generated__/userData';
import { ADD_USER_SETTING } from '../mutation/userData';

const excludedColumns = ['selection', 'icons'];

// A saved order can hold fields that were never valid for its table: ENG-3396
// found the marine page persisting marineColumnsOrder under the HOME key, and
// a field can also simply be removed from a config later. Anything without a
// config entry for THIS asset class is dropped before it can reach the table.
const sanitizeLoanTableOrder = <K extends keyof AssetClassToLoanType>(
  key: K,
  saved: unknown,
): string[] => {
  const config = assetClassToColumnsConfig[key]?.config as Record<string, unknown> | undefined;
  return Array.isArray(saved) ? saved.filter((field) => !!config?.[String(field)]) : [];
};

export const checkColumnCfgPayload = (userSettings: GlobalTableOrderConfig) => {
  // A saved order must not win wholesale: it froze the column SET at the moment
  // the user first dragged a column, permanently hiding every column added to
  // the defaults afterwards (the ENG-3391 UW TYPE column would never appear for
  // them). Keep the user's ordering, append default columns they don't have.
  const fallback = <T>(val: T | undefined, fallbackVal: T): T =>
    (Array.isArray(val) && val.length
      ? [...val, ...(fallbackVal as unknown[]).filter((c) => !val.includes(c))]
      : fallbackVal) as T;

  const mergeLoanTable = <K extends keyof AssetClassToLoanType>(
    key: K,
    defaults: (keyof AssetClassToLoanType[K])[],
  ): (keyof AssetClassToLoanType[K])[] =>
    fallback(
      sanitizeLoanTableOrder(
        key,
        userSettings?.myPortfolio?.loanTable?.[key],
      ) as (keyof AssetClassToLoanType[K])[],
      defaults,
    );

  return {
    myPortfolio: {
      loanTable: {
        HOME: mergeLoanTable(GqlAssetClass.HOME, homeARMColumnsOrder),
        HOME_ARM: mergeLoanTable('HOME_ARM', homeARMColumnsOrder),
        UNSECPERSONAL: mergeLoanTable(GqlAssetClass.UNSECPERSONAL, unsecpersonalColumnsOrder),
        AUTO: mergeLoanTable(GqlAssetClass.AUTO, autoColumnsOrder),
        HELOC: mergeLoanTable(GqlAssetClass.HELOC, helocColumnsOrder),
        CRE: mergeLoanTable(GqlAssetClass.CRE, creColumnsOrder),
        MARINE: mergeLoanTable(GqlAssetClass.MARINE, marineColumnsOrder),
      },
    },
    marketPlace: {
      marketHomeTable: fallback(userSettings?.marketPlace?.marketHomeTable, [
        'view-deal',
        ...listingColsOrder,
      ]) as marketHomeTableColumnsOrder[],
      activeTrades: {
        allTradesTable: fallback(
          userSettings?.marketPlace?.activeTrades?.allTradesTable,
          activeTradesAllTradesColumnOrder,
        ),
        bidsTable: fallback(
          userSettings?.marketPlace?.activeTrades?.bidsTable,
          activeTradesBidsListingColsOrder,
        ),
      },

      executedTradesTable: fallback(
        userSettings?.marketPlace?.executedTradesTable,
        executedTradesColumnOrder,
      ),
    },
  };
};

export type AssetClassTableNames =
  | 'loanTable_HOME'
  | 'loanTable_HOME_ARM'
  | 'loanTable_AUTO'
  | 'loanTable_UNSECPERSONAL'
  | 'loanTable_CRE'
  | 'loanTable_HELOC'
  | 'loanTable_MARINE'
  | 'marketHomeTable'
  | 'allTradesTable'
  | 'bidsTable'
  | 'executedTradesTable';

export const useColumnOrder = (tableName: AssetClassTableNames, currentOrder: string[]) => {
  const { state, dispatch } = useContext(DataContext);

  const [addUserSetting] = useMutation<AddUserSetting>(ADD_USER_SETTING);

  const setColumnOrder = useCallback(
    async (order: Updater<string[]>) => {
      const resolvedOrder = typeof order === 'function' ? order(currentOrder as string[]) : order;

      let payload;
      let resolvedCfgOrder = resolvedOrder;

      switch (tableName) {
        case 'loanTable_HOME':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                HOME: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (homeARMColumnsOrder as (keyof AssetClassToLoanType['HOME'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_HOME_ARM':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                HOME_ARM: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (homeARMColumnsOrder as (keyof AssetClassToLoanType['HOME_ARM'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_CRE':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                CRE: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (creColumnsOrder as (keyof AssetClassToLoanType['CRE'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_HELOC':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                HELOC: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (helocColumnsOrder as (keyof AssetClassToLoanType['HELOC'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_MARINE':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                MARINE: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (marineColumnsOrder as (keyof AssetClassToLoanType['MARINE'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_AUTO':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                AUTO: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (autoColumnsOrder as (keyof AssetClassToLoanType['AUTO'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'loanTable_UNSECPERSONAL':
          payload = {
            ...state.globalTableOrderConfig,
            myPortfolio: {
              loanTable: {
                ...state.globalTableOrderConfig.myPortfolio.loanTable,
                UNSECPERSONAL: resolvedOrder.filter((item) => item !== 'selection')?.length
                  ? resolvedOrder.filter((item) => item !== 'selection')
                  : (unsecpersonalColumnsOrder as (keyof AssetClassToLoanType['UNSECPERSONAL'])[]),
              },
            },
          } as GlobalTableOrderConfig;

          resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
          break;

        case 'marketHomeTable':
          payload = {
            ...state.globalTableOrderConfig,
            marketPlace: {
              ...state.globalTableOrderConfig.marketPlace,
              marketHomeTable: resolvedOrder as ListingKey[],
            },
          } as GlobalTableOrderConfig;
          break;

        case 'allTradesTable':
          payload = {
            ...state.globalTableOrderConfig,
            marketPlace: {
              ...state.globalTableOrderConfig.marketPlace,
              activeTrades: {
                ...state.globalTableOrderConfig.marketPlace.activeTrades,
                allTradesTable: resolvedOrder as string[],
              },
            },
          } as GlobalTableOrderConfig;
          break;

        case 'bidsTable':
          payload = {
            ...state.globalTableOrderConfig,
            marketPlace: {
              ...state.globalTableOrderConfig.marketPlace,
              activeTrades: {
                ...state.globalTableOrderConfig.marketPlace.activeTrades,
                bidsTable: resolvedOrder as string[],
              },
            },
          } as GlobalTableOrderConfig;
          break;

        case 'executedTradesTable':
          payload = {
            ...state.globalTableOrderConfig,
            marketPlace: {
              ...state.globalTableOrderConfig.marketPlace,
              executedTradesTable: resolvedOrder as string[],
            },
          } as GlobalTableOrderConfig;
          break;

        default:
          return;
      }

      if (!isEqual(resolvedCfgOrder, currentOrder)) {
        dispatch({
          type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
          payload,
        });

        try {
          await addUserSetting({
            variables: {
              name: 'globalTableOrderConfig',
              value: JSON.stringify(payload),
            },
          });
        } catch {
          toast.error('Column order failed');
        }
      }
    },
    [addUserSetting, currentOrder, dispatch, state.globalTableOrderConfig, tableName],
  );

  return {
    setColumnOrder,
  };
};
